// client/transports/websocketTransport.ts
import { A } from "@adornis/base/env-info";
import { ignoreSymbol, setClientChildLogger } from "@adornis/base/logging";
import { BehaviorSubject, lastValueFrom, Observable } from "rxjs";
import { filter, share, switchMap, take } from "rxjs/operators";
import sockjsClient from "sockjs-client/dist/sockjs";
import "../../metaDataStore";
import {
  MessageTypes
} from "../../protocol";
import "../../requestHandler";
import { combineErrors } from "../../utils/helpers";
import { BaseQLClient } from "../baseqlClient";
import Cookies from "js-cookie";
var _BaseQLWebsocketTransport = class {
  constructor(initPayload, options) {
    this.initPayload = initPayload;
    this.options = options;
    this.sessionID = localStorage.getItem("sessionID") ?? "";
    this.connectionID = "";
    this.available = true;
    this.destroyed = false;
    this._session = new BehaviorSubject(false);
    this._operationCounter = 0;
    this._subscriptionObservables = /* @__PURE__ */ new Map();
    this._online = navigator.onLine;
    this._connectionTries = 0;
    this.mutation = this.query;
    console.debug("Constructing websocketTransport");
    this._connect();
    this._onlineDetectionSub = A.online().subscribe((on) => {
      this._online = on;
      if (this._client && on === false) {
        this._client.close();
      }
      if (!this._client && on) {
        this._reconnect();
      }
    });
  }
  static get(initPayload, options) {
    return this.instance || (this.instance = new _BaseQLWebsocketTransport(initPayload, options));
  }
  _connect(reconnect = false) {
    this.available = true;
    this._client = new sockjsClient(this.options?.endpoint ?? A.absoluteUrl("/api/graphql-sockjs"), null, {
      timeout: 2e3,
      transports: [
        `websocket`,
        "xhr-polling",
        "xhr-streaming",
        "xdr-streaming",
        "eventsource",
        "iframe-eventsource",
        "htmlfile",
        "iframe-htmlfile",
        "xdr-polling",
        "iframe-xhr-polling",
        "jsonp-polling"
      ]
    });
    this._client.onopen = async () => {
      this._connectionTries = 0;
      this.startSession(this.initPayload ?? { sessionID: this.sessionID }, reconnect);
      await lastValueFrom(this._waitForSession());
      if (reconnect)
        this.restartOpenOperations();
    };
    this._client.onmessage = (e) => {
      const message = JSON.parse(e.data);
      let sub;
      switch (message.type) {
        case MessageTypes.GQL_DATA:
          sub = this._subscriptionObservables.get(message.id);
          if (!sub)
            return console.warn("No subject stored for Operation ID " + message.id);
          console.debug("BaseQL websocket: data", message.id, sub.queryName);
          sub.sub.next(message.payload);
          return;
        case MessageTypes.GQL_ERROR:
          sub = this._subscriptionObservables.get(message.id);
          if (!sub)
            return console.warn("No subject stored for Operation ID " + message.id);
          sub.serverCompleted = true;
          sub.sub.error(message.payload.errors);
          this._subscriptionObservables.delete(message.id);
          return;
        case MessageTypes.GQL_COMPLETE:
          sub = this._subscriptionObservables.get(message.id);
          if (!sub)
            return console.warn("No subject stored for Operation ID " + message.id);
          console.debug("BaseQL websocket: complete", message.id, sub.queryName);
          sub.serverCompleted = true;
          sub.sub.complete();
          this._subscriptionObservables.delete(message.id);
          return;
        case MessageTypes.GQL_CONNECTION_ACK:
          this.sessionID = message.payload.sessionID;
          BaseQLClient.sessionID = message.payload.sessionID;
          BaseQLClient.connectionID = message.payload.connectionID;
          Cookies.set("baseQLSession", message.payload.sessionID);
          setClientChildLogger({ sessionID: message.payload.sessionID });
          localStorage.setItem("sessionID", message.payload.sessionID);
          this._session.next(true);
          console.debug("BaseQL websocket: Session opened", message.payload.sessionID);
          return;
        case MessageTypes.GQL_CONNECTION_ACK_CLOSE:
          console.debug("BaseQL websocket: Session closed");
          return;
        case MessageTypes.GQL_CONNECTION_ERROR:
          console.debug("BaseQL websocket: Connection ERR");
          const combinedError = combineErrors(message.payload.errors);
          console.error(
            `%c\u{1F32A}\uFE0F error from the server on session creation`,
            "color: white; font-size: 1.6em; font-weight: bold",
            `
${combinedError}`
          );
          combinedError[ignoreSymbol] = true;
          throw combinedError;
        case MessageTypes.GQL_CONNECTION_KEEP_ALIVE:
          return;
        default:
          return;
      }
    };
    this._client.onerror = (e) => {
      console.log(e);
      this._reconnect();
    };
    this._client.onclose = (e) => {
      console.log(e);
      this._reconnect();
    };
  }
  _reconnect() {
    this._session.next(false);
    delete this._client;
    if (!this._online)
      return;
    this._connectionTries++;
    clearTimeout(this._reconnectTimeout);
    this._reconnectTimeout = setTimeout(() => {
      this._connect(true);
    }, 1e3 + Math.pow(2, this._connectionTries) * 50);
  }
  _waitForSession() {
    return this._session.pipe(
      filter((v) => v),
      take(1)
    );
  }
  _subscribe(op, payload) {
    const id = ++this._operationCounter + "";
    const obs = new Observable((subscriber) => {
      this._subscriptionObservables.set(id, {
        sub: subscriber,
        serverCompleted: false,
        queryName: op.name,
        payload
      });
      return payload.subscription ? () => this._unsubscribe(id) : void 0;
    });
    console.debug("BaseQL websocket: sending op", id, op.name);
    this._client?.send(JSON.stringify({ id, type: MessageTypes.GQL_START, payload }));
    return obs;
  }
  _unsubscribe(id) {
    const sub = this._subscriptionObservables.get(id);
    if (!sub)
      return;
    if (!sub.serverCompleted)
      this._client?.send(JSON.stringify({ id, type: MessageTypes.GQL_STOP }));
    sub.sub.complete();
  }
  restartOpenOperations() {
    for (const [id, operation] of this._subscriptionObservables) {
      console.debug("restarting op", id);
      this._client?.send(
        JSON.stringify({ id, type: MessageTypes.GQL_START, payload: operation.payload, reconnect: true })
      );
    }
  }
  subscription(op, payload) {
    return this._waitForSession().pipe(
      switchMap(() => this._subscribe(op, payload)),
      share()
    );
  }
  async query(op, payload) {
    if (payload.subscription)
      throw new Error("query/mutation cannot specify a subscription!");
    if (!payload.query && !payload.mutation)
      throw new Error("operation has to specify a query or a mutation!");
    await lastValueFrom(this._waitForSession());
    const obs = this._subscribe(op, payload);
    return lastValueFrom(obs);
  }
  stop() {
  }
  destroy() {
    if (this.destroyed)
      throw new Error("Destroying an already destroyed BaseQLWebsocketTransport");
    this.cleanup();
    this._onlineDetectionSub.unsubscribe();
    this.destroyed = true;
  }
  closeSession() {
    this._session.next(false);
    this.cleanup();
    this._client?.send(JSON.stringify({ type: MessageTypes.GQL_CONNECTION_CLOSE }));
  }
  cleanup() {
    this._client?.close();
    this._subscriptionObservables.forEach((sub, id) => this._unsubscribe(id));
    this._subscriptionObservables.clear();
  }
  startSession(payload, reconnect = false) {
    if (this._session.getValue() && !reconnect)
      this.closeSession();
    this._client?.send(JSON.stringify({ type: MessageTypes.GQL_CONNECTION_INIT, payload }));
  }
  terminate() {
    this._session.next(false);
    this.closeSession();
    this._client?.send(JSON.stringify({ type: MessageTypes.GQL_CONNECTION_TERMINATE }));
  }
};
var BaseQLWebsocketTransport = _BaseQLWebsocketTransport;
BaseQLWebsocketTransport.instance = void 0;
export {
  BaseQLWebsocketTransport
};
